/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';

import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import type { Match } from '@atlassian/react-resource-router';
import { JiraSiteAri } from '@atlassian/ari/jira/site';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import parameters from './__generated__/WorkItemEntrypointUiQuery$parameters.ts';
import type { WorkItemEntrypointUiQuery } from './__generated__/WorkItemEntrypointUiQuery.graphql.ts';

const CHILD_ISSUES_INITIAL_DISPLAY_COUNT = 5;

/** Forge module types for work item query. Keep in sync with workItemForgeTypes in @jira/work-item. */
const WORK_ITEM_FORGE_TYPES = [
	'jira:issueAction',
	'jira:issueGlance',
	'jira:issueContext',
	'jira:issuePanel',
	'jira:issueActivity',
	'jira:customField',
	'jira:customFieldType',
	'jira:issueViewBackgroundScript',
	'jira:uiModifications',
] as const;

const getIssueKey = (match: Match) => {
	return match?.params?.issueKey?.length ? match.params.issueKey : null;
};

const extractProjectKey = (issueKey: string): string => issueKey.split('-')[0];

type WorkItemEntrypointComponent = EntryPointComponent<
	{
		workItemQuery: WorkItemEntrypointUiQuery;
	},
	{},
	{},
	{}
>;

export const workItemEntrypoint: EntryPoint<WorkItemEntrypointComponent, EntryPointRouteParams> =
	createEntryPoint({
		root: JSResourceForUserVisible<WorkItemEntrypointComponent>(
			/* webpackChunkName: "async-work-item-view-entrypoint" */ './WorkItemSanitisedEntrypoint.tsx',
			{
				entryFsSsrLiftImportToModule: true,
			},
		),
		getPreloadProps: ({
			context,
			tenantContext: { cloudId, isAnonymous },
		}: EntryPointRouteParams) => {
			const { match } = context;
			const issueKey = getIssueKey(match);
			const projectKey = issueKey ? extractProjectKey(issueKey) : '';
			const jql = projectKey ? `project = "${projectKey}"` : '';
			const focusedCommentId = context.query?.focusedCommentId
				? String(context.query.focusedCommentId)
				: null;
			// Logic from getChildIssuesFieldIdsQueryVariables (main-issue-agg-query). fg() only in `if`
			// conditions — not inside `const queryVariables = { fg() }` (jira/ff/inline-usage).
			const childIssuesFieldIds = ['issuetype'];
			if (!fg('relay-migration-edit-worklog') && !fg('jpx-93-unship-legacy-timetracking')) {
				childIssuesFieldIds.push('timetracking');
			}
			if (!fg('jira-issue-view-child-issues-panel-realtime-update')) {
				childIssuesFieldIds.push('status');
			}
			// Under streaming SSR this query can be aborted at the SSR timeout. `store-and-network` would
			// then render from whatever another query left in the store for this issue, giving a work item
			// view with null fields instead of a skeleton. `network-only` makes the aborted read fail
			// instead, so the boundary shows its fallback. The client keeps cache-first transitions.
			let fetchPolicy: 'store-and-network' | 'network-only' = 'store-and-network';
			if (__SERVER__ && fg('jfp-magma-ssr-streaming-enable-work-item')) {
				fetchPolicy = 'network-only';
			}
			// Design panel + child-issue vars + master preload fields (issueTypeId / favourite fields).
			return {
				queries: {
					workItemQuery: {
						options: {
							fetchPolicy,
						},
						variables: {
							cloudId: cloudId || '',
							issueKey: issueKey || '',
							projectKey,
							jql,
							siteAri: cloudId ? JiraSiteAri.create({ siteId: cloudId }).toString() : '',
							// Design panel data is fetched via connectedToIssueAggDataResource
							// (ConnectedToIssueAggQuery) and passed into WorkItemLayout as connectedAggData.
							// These are false to remove the fields from the main blocking query.
							consistentRead: false,
							includeDesignPanelData: false,
							includeFigmaConfigData: fg('remove_ctiaq_config_state')
								? Boolean(!isAnonymous)
								: false,
							commentTargetId: focusedCommentId,
							hasCommentTargetId: !!focusedCommentId,
							applicationKey: 'JIRA_SOFTWARE' as const,
							issueViewAddAttachmentFlag: false,
							forgeTypes: [...WORK_ITEM_FORGE_TYPES],
							issueSearchInput: fg('jira-issue-view-child-work-items-ssr-fg')
								? { jql: issueKey ? `parent = ${issueKey}` : '' }
								: { childIssuesInput: { parentIssueKey: issueKey || '' } },
							fieldSetsInput: {
								viewInput: {
									namespace: 'CHILD_ISSUE_PANEL',
									context: { issueContext: { issueKey } },
								},
							},
							childIssuesFieldIds,
							childIssuesAmountOfColumns: 500,
							childIssuesFirst: fg('jira_child_work_items_pagination')
								? CHILD_ISSUES_INITIAL_DISPLAY_COUNT
								: undefined,
							childWorkItemsSSRUIEnabled: Boolean(fg('jira-issue-view-child-work-items-ssr-fg')),
							shouldUseNewChildWorkItemsDatafetcher: true,
							isInitialLoadQuery: true,
							shouldCaptureEntrypointQuery5xx: Boolean(
								fg('wi2-work-item-entrypoint-query-5xx-capture'),
							),
							includeAgenticWorkItemData: Boolean(
								expVal('work_item_for_quadratic', 'isEnabled', false),
							),
							sideBarLayoutDecompositionEnabled: Boolean(fg('side-layout-work-item-integration')),
						},
						parameters,
					},
				},
			};
		},
	});
